import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ClipboardList, FileCheck2, ExternalLink, UserPlus, Trash2,
  Pencil, MoreHorizontal, MailCheck, ArrowRight, Send,
} from "lucide-react";
import { formatDateTime } from "@/lib/ui";

/**
 * Client Profile → Forms
 * ----------------------
 * Three sections per spec:
 *   1. Assigned to Client  — forms the client can fill from their secure portal
 *   2. Available Forms     — org form templates. Two clear actions per template:
 *                            "Fill Form" (staff fills for client) and "Assign to Client"
 *   3. Completed Forms     — historical submitted responses; click row to open the
 *                            actual answers (not a blank template)
 *
 * Historical responses linked to archived templates STILL appear in Completed
 * Forms — the archive gate only affects Available Forms / Assign selection.
 */
export default function FormsTab({ clientId, client, isReadOnly, onChange }) {
  const { currentOrgId } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [responses, setResponses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [fillOpen, setFillOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigningTpl, setAssigningTpl] = useState("");

  const load = async () => {
    setLoading(true);
    const [tpl, res, asg] = await Promise.all([
      supabase.from("form_templates").select("*")
        .eq("organization_id", currentOrgId).eq("status", "active").order("name"),
      supabase.from("form_responses").select("*, form_templates(name, fields, status)")
        .eq("client_id", clientId).order("submitted_at", { ascending: false }),
      apiFetch(`/clients/${clientId}/form-assignments`).catch(() => []),
    ]);
    setTemplates(tpl.data || []);
    setResponses(res.data || []);
    setAssignments(Array.isArray(asg) ? asg : []);
    setLoading(false);
  };
  useEffect(() => { if (clientId && currentOrgId) load(); /* eslint-disable-next-line */ }, [clientId, currentOrgId]);

  const startFill = (t) => {
    setSelectedTemplate(t);
    const initial = {};
    (t.fields || []).forEach((f) => {
      if (f.map_to?.startsWith("client.")) {
        const col = f.map_to.split(".")[1];
        if (client && client[col] != null) initial[f.id || f.label] = String(client[col]);
      }
    });
    setAnswers(initial);
    setFillOpen(true);
  };

  const openAssignFor = (tplId) => { setAssigningTpl(tplId || ""); setAssignOpen(true); };

  const submit = async () => {
    if (!selectedTemplate) return;
    // Client-side required-field check for a friendlier UX
    const missing = (selectedTemplate.fields || [])
      .filter((f) => f.required && !String(answers[f.id || f.label] ?? "").trim())
      .map((f) => f.label);
    if (missing.length) return toast.error(`Fill required: ${missing.join(", ")}`);
    try {
      await apiFetch(`/forms/${selectedTemplate.id}/submit`, {
        method: "POST",
        body: JSON.stringify({ form_template_id: selectedTemplate.id, client_id: clientId, responses: answers }),
      });
      toast.success("Form submitted — client record auto-populated where mapped");
      setFillOpen(false); setSelectedTemplate(null); setAnswers({});
      load(); onChange && onChange();
    } catch (e) {
      // Humanize backend error codes for staff. Never leak raw internal strings.
      const raw = String(e?.message || "");
      const MAP = {
        forced_test_failure: "Test-only failure — nothing was saved.",
        form_not_found: "This form template no longer exists.",
        missing_required_fields: "Please fill the fields marked with *.",
      };
      const msg = MAP[raw] || (raw && raw.length < 100 && !raw.match(/^[a-z_]+$/) ? raw : "Something went wrong — your response was not saved. Please try again.");
      toast.error(msg);
    }
  };

  const openView = (r) => { setViewing(r); setViewOpen(true); };

  const assignForm = async () => {
    if (!assigningTpl) return;
    try {
      await apiFetch("/form-assignments", {
        method: "POST",
        body: JSON.stringify({ organization_id: currentOrgId, form_template_id: assigningTpl, client_id: clientId }),
      });
      toast.success("Form assigned — it will appear in the client's portal");
      setAssignOpen(false); setAssigningTpl("");
      load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const unassign = async (id) => {
    if (!window.confirm("Remove this form from the client's portal?")) return;
    try {
      await apiFetch(`/form-assignments/${id}`, { method: "DELETE" });
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const sendReminder = async (id) => {
    try {
      await apiFetch(`/form-assignments/${id}/reminder`, { method: "POST" });
      toast.success("Reminder sent");
    } catch (e) {
      const raw = String(e?.message || "");
      const MAP = {
        already_submitted: "This form was already submitted — no reminder needed.",
        client_has_no_email: "This client does not have an email on file.",
        rate_limited: "Too many reminders in the last hour — please wait a bit.",
        email_send_failed: "The reminder could not be sent right now. Try again shortly.",
        portal_link_failed: "Could not build a portal link for this client.",
      };
      toast.error(MAP[raw] || raw || "Reminder not sent");
    }
  };

  const [reminderPreview, setReminderPreview] = useState(null); // { assignmentId, subject, to, form_name, portal_url, html_body }
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);

  const openReminderPreview = async (assignmentId) => {
    setReminderLoading(true);
    setReminderPreview({ assignmentId }); // open dialog immediately with a spinner
    try {
      const r = await apiFetch(`/form-assignments/${assignmentId}/reminder/preview`);
      setReminderPreview({ ...r, assignmentId });
    } catch (e) {
      const raw = String(e?.message || "");
      const MAP = {
        already_submitted: "This form was already submitted — no reminder needed.",
        client_has_no_email: "This client does not have an email on file.",
        portal_link_failed: "Could not build a portal link for this client.",
      };
      toast.error(MAP[raw] || raw || "Preview unavailable");
      setReminderPreview(null);
    } finally { setReminderLoading(false); }
  };

  const confirmSendReminder = async () => {
    if (!reminderPreview?.assignmentId) return;
    setReminderSending(true);
    try {
      await apiFetch(`/form-assignments/${reminderPreview.assignmentId}/reminder`, { method: "POST" });
      toast.success(`Reminder sent to ${reminderPreview.to}`);
      setReminderPreview(null);
    } catch (e) {
      const raw = String(e?.message || "");
      const MAP = {
        already_submitted: "This form was already submitted — no reminder needed.",
        client_has_no_email: "This client does not have an email on file.",
        rate_limited: "Too many reminders in the last hour — please wait a bit.",
        email_send_failed: "The reminder could not be sent right now. Try again shortly.",
      };
      toast.error(MAP[raw] || raw || "Reminder not sent");
    } finally { setReminderSending(false); }
  };

  if (loading) return <div className="text-center py-12 text-sm text-[#57534E]">Loading forms…</div>;

  return (
    <div className="space-y-8" data-testid="forms-tab">

      {/* ============ 1. ASSIGNED TO CLIENT ============ */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-lg tracking-tight">Assigned to Client</h3>
            <p className="text-xs text-[#57534E] mt-0.5">Forms this client can complete through their secure Client Portal.</p>
          </div>
          {!isReadOnly && (
            <Button size="sm" data-testid="assign-form-btn" onClick={() => openAssignFor("")}
                    className="rounded-full bg-[#2C3A29] hover:bg-[#3E4C38] text-white">
              <UserPlus size={13} className="mr-1" /> Assign Form
            </Button>
          )}
        </div>

        {assignments.length === 0 ? (
          <EmptyCard icon={ClipboardList} title="No forms assigned to this client yet.">
            {!isReadOnly && (
              <Button size="sm" onClick={() => openAssignFor("")} data-testid="assign-form-empty-btn"
                      className="mt-3 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
                <UserPlus size={13} className="mr-1" /> Assign Form
              </Button>
            )}
          </EmptyCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {assignments.map((a) => (
              <AssignmentCard key={a.id} a={a} responses={responses}
                onOpenResponse={openView}
                onSendReminder={() => openReminderPreview(a.id)}
                onUnassign={() => unassign(a.id)}
                readOnly={isReadOnly} />
            ))}
          </div>
        )}
      </section>

      {/* ============ 2. AVAILABLE FORMS ============ */}
      <section>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-lg tracking-tight">Available Forms</h3>
            <p className="text-xs text-[#57534E] mt-0.5">Select a form to complete for this client or assign it to their portal.</p>
          </div>
          <Link to="/app/forms" data-testid="manage-templates-link"
                className="text-xs text-[#E8712C] hover:underline inline-flex items-center gap-1">
            Manage Form Templates <ExternalLink size={11} />
          </Link>
        </div>

        {templates.length === 0 ? (
          <EmptyCard icon={ClipboardList} title="No active form templates yet.">
            <Link to="/app/forms" className="text-xs text-[#E8712C] hover:underline mt-2 inline-block">
              Create your first form template
            </Link>
          </EmptyCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <div key={t.id} data-testid={`template-card-${t.id}`}
                   className="bg-white rounded-2xl border border-[#E8E2D9] p-4 space-y-3">
                <div className="min-w-0">
                  <div className="flex items-start gap-2">
                    <ClipboardList size={16} className="text-[#E8712C] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{t.name}</div>
                      <div className="text-xs text-[#57534E] line-clamp-2">
                        {t.description || `${(t.fields || []).length} field${(t.fields || []).length === 1 ? "" : "s"}`}
                      </div>
                    </div>
                  </div>
                </div>
                {!isReadOnly && (
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" data-testid={`fill-form-${t.id}`} onClick={() => startFill(t)}
                            className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white flex-1 min-w-[110px]">
                      <Pencil size={12} className="mr-1" /> Fill Form
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`assign-template-${t.id}`}
                            onClick={() => openAssignFor(t.id)}
                            className="rounded-full flex-1 min-w-[130px]">
                      <UserPlus size={12} className="mr-1" /> Assign to Client
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ============ 3. COMPLETED FORMS ============ */}
      <section>
        <h3 className="font-display font-semibold text-lg tracking-tight mb-1">Completed Forms</h3>
        <p className="text-xs text-[#57534E] mb-3">
          Historical responses for this client. Includes responses from templates that were later archived.
        </p>

        {responses.length === 0 ? (
          <EmptyCard icon={FileCheck2} title="No completed forms yet."
                     subtitle="Completed forms will appear here after the client or staff submits a form." />
        ) : (
          <div className="bg-white rounded-2xl border border-[#E8E2D9] divide-y divide-[#E8E2D9]">
            {responses.map((r) => {
              const archived = r.form_templates?.status === "archived";
              return (
                <button key={r.id} onClick={() => openView(r)} data-testid={`view-response-${r.id}`}
                  className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-[#FAF8F5] transition-colors">
                  <FileCheck2 size={16} className="text-[#2C3A29] flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.form_templates?.name || "Form"}
                      {archived && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-[#A8A29E] px-1.5 py-0.5 border border-[#E8E2D9] rounded-full">
                          archived template
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#57534E]">Submitted {formatDateTime(r.submitted_at)}</div>
                  </div>
                  <ArrowRight size={14} className="text-[#A8A29E]" />
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ============ Assign Dialog ============ */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Assign a form to this client</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">
              The client will see it in their secure portal and can complete it at their own pace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Form template</Label>
            <Select value={assigningTpl} onValueChange={setAssigningTpl}>
              <SelectTrigger data-testid="assign-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-assign-btn" onClick={assignForm} disabled={!assigningTpl}
                    className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Fill Dialog (staff fills on behalf of client) ============ */}
      <Dialog open={fillOpen} onOpenChange={setFillOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{selectedTemplate?.name}</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">
              You are completing this form on behalf of {client?.first_name} {client?.last_name}. Mapped fields will auto-populate the client record.
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {selectedTemplate.description && <p className="text-sm text-[#57534E]">{selectedTemplate.description}</p>}
              {(selectedTemplate.fields || []).map((f, i) => (
                <FieldInput key={f.id || i} field={f} value={answers[f.id || f.label] ?? ""}
                  onChange={(v) => setAnswers({ ...answers, [f.id || f.label]: v })} />
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={() => setFillOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="submit-form-btn" onClick={submit}
                    className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              <Send size={12} className="mr-1" /> {selectedTemplate?.submit_button_label || "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ View Response Dialog ============ */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{viewing?.form_templates?.name}</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">
              Submitted {viewing?.submitted_at ? formatDateTime(viewing.submitted_at) : "—"}. This is the original response as submitted.
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              {(viewing.form_templates?.fields || []).length === 0 && (
                <div className="text-xs text-[#A8A29E]">The original template has no field definitions cached — showing raw answers.</div>
              )}
              {(viewing.form_templates?.fields || []).length > 0
                ? (viewing.form_templates.fields || []).map((f, i) => {
                    const val = viewing.responses?.[f.id || f.label];
                    return (
                      <div key={f.id || i} className="border-b border-[#E8E2D9] pb-3">
                        <div className="text-xs text-[#A8A29E] uppercase tracking-widest">{f.label}</div>
                        <div className="text-sm text-[#1C1917] mt-0.5 whitespace-pre-wrap">
                          {(val === "" || val == null) ? <span className="text-[#A8A29E]">—</span> : String(val)}
                        </div>
                      </div>
                    );
                  })
                : Object.entries(viewing.responses || {}).map(([k, v]) => (
                    <div key={k} className="border-b border-[#E8E2D9] pb-3">
                      <div className="text-xs text-[#A8A29E] uppercase tracking-widest">{k}</div>
                      <div className="text-sm text-[#1C1917] mt-0.5 whitespace-pre-wrap">{String(v ?? "—")}</div>
                    </div>
                  ))
              }
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setViewOpen(false)} variant="outline" className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ============ Reminder Preview Dialog ============ */}
      <Dialog open={!!reminderPreview} onOpenChange={(v) => !v && setReminderPreview(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="reminder-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Send reminder email?</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">
              Preview exactly what the client will receive before you send.
            </DialogDescription>
          </DialogHeader>
          {reminderLoading || !reminderPreview?.subject ? (
            <div className="py-6 text-center text-sm text-[#A8A29E]">Loading preview…</div>
          ) : (
            <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-[80px_1fr] gap-2 items-baseline">
                <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">To</div>
                <div data-testid="reminder-to">
                  <div className="text-[#1C1917]">{reminderPreview.client_name}</div>
                  <div className="text-xs text-[#57534E]">{reminderPreview.to}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Subject</div>
                <div className="font-medium text-[#1C1917]" data-testid="reminder-subject">{reminderPreview.subject}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Form</div>
                <div className="text-[#1C1917]" data-testid="reminder-form">{reminderPreview.form_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]">Link</div>
                <a href={reminderPreview.portal_url} target="_blank" rel="noreferrer"
                   data-testid="reminder-portal-link"
                   className="text-xs text-[#E8712C] hover:underline break-all">{reminderPreview.portal_url}</a>
              </div>
              <div className="border border-[#E8E2D9] rounded-2xl p-3 bg-[#FAF8F5]">
                <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-2">Body</div>
                <div className="prose prose-sm max-w-none email-preview" data-testid="reminder-body"
                     dangerouslySetInnerHTML={{ __html: reminderPreview.html_body }} />
              </div>
              <p className="text-[10px] text-[#A8A29E]">Sending is rate-limited to 3 reminders per hour per assignment.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReminderPreview(null)}
                    className="rounded-full" data-testid="reminder-cancel">Cancel</Button>
            <Button onClick={confirmSendReminder}
                    disabled={reminderLoading || reminderSending || !reminderPreview?.subject}
                    data-testid="reminder-send"
                    className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
              {reminderSending ? "Sending…" : "Send Reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** A single assignment card. Opens the completed response if one exists,
 *  otherwise the assignment can be reminded/unassigned by staff. */
function AssignmentCard({ a, responses, onOpenResponse, onSendReminder, onUnassign, readOnly }) {
  const submitted = a.status === "submitted";
  const inProgress = a.status === "in_progress";
  const [menu, setMenu] = useState(false);
  const linkedResponse = a.submitted_response_id
    ? responses.find((r) => r.id === a.submitted_response_id)
    : null;

  const openLinked = () => {
    if (linkedResponse) onOpenResponse(linkedResponse);
    else toast.error("No submitted response yet.");
  };

  return (
    <div data-testid={`assignment-${a.id}`}
         className="bg-white rounded-2xl border border-[#E8E2D9] p-4 space-y-3 relative">
      <div className="flex items-start gap-2">
        <ClipboardList size={16} className="text-[#2C3A29] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{a.form_templates?.name || "Form"}</div>
          <div className="text-xs text-[#57534E]">
            Assigned {formatDateTime(a.created_at)}
            {a.submitted_at && ` · Submitted ${formatDateTime(a.submitted_at)}`}
          </div>
        </div>
        <StatusPill status={a.status} />
      </div>
      <div className="flex gap-2 flex-wrap">
        {submitted ? (
          <Button size="sm" onClick={openLinked} data-testid={`open-assignment-${a.id}`}
                  disabled={!linkedResponse}
                  className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white flex-1 min-w-[100px]">
            Open <ArrowRight size={12} className="ml-1" />
          </Button>
        ) : (
          <>
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={onSendReminder}
                      data-testid={`remind-assignment-${a.id}`}
                      className="rounded-full flex-1 min-w-[130px]">
                <MailCheck size={12} className="mr-1" /> Send Reminder
              </Button>
            )}
          </>
        )}
        {!readOnly && (
          <div className="relative">
            <button data-testid={`assignment-menu-${a.id}`}
                    onClick={() => setMenu((v) => !v)}
                    className="h-9 w-9 rounded-full inline-flex items-center justify-center border border-[#E8E2D9] hover:border-[#E8712C]">
              <MoreHorizontal size={14} />
            </button>
            {menu && (
              <div className="absolute right-0 top-10 z-10 bg-white border border-[#E8E2D9] rounded-xl shadow-lg py-1 min-w-[160px]">
                {!submitted && (
                  <button onClick={() => { setMenu(false); onUnassign(); }}
                          data-testid={`unassign-${a.id}`}
                          className="w-full text-left text-xs px-3 py-2 hover:bg-[#FAF8F5] text-[#B02222] inline-flex items-center gap-2">
                    <Trash2 size={12} /> Unassign
                  </button>
                )}
                {submitted && linkedResponse && (
                  <button onClick={() => { setMenu(false); openLinked(); }}
                          className="w-full text-left text-xs px-3 py-2 hover:bg-[#FAF8F5]">View response</button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {inProgress && (
        <div className="text-[10px] text-[#C55A1F] uppercase tracking-widest">Client has started but not submitted</div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    submitted:   { label: "Submitted",   cls: "bg-[#E8F0E4] text-[#2C3A29]" },
    in_progress: { label: "In Progress", cls: "bg-[#FDE9D8] text-[#C55A1F]" },
    not_started: { label: "Not Started", cls: "bg-[#F5F5F4] text-[#57534E]" },
  };
  const s = map[status] || map.not_started;
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

function EmptyCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="text-center py-8 bg-white rounded-2xl border border-[#E8E2D9]">
      <Icon size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
      <p className="text-sm text-[#57534E]">{title}</p>
      {subtitle && <p className="text-xs text-[#A8A29E] mt-1">{subtitle}</p>}
      {children}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const key = field.id || field.label;
  const common = {
    id: key,
    "data-testid": `form-field-${key}`,
    value,
    onChange: (e) => onChange(e.target.value),
    placeholder: field.placeholder || "",
  };
  const label = <Label htmlFor={key} className="text-sm">{field.label}{field.required && <span className="text-[#E8712C]"> *</span>}</Label>;

  if (field.type === "textarea") return <div>{label}<Textarea rows={3} {...common} className="mt-1.5" /></div>;
  if (field.type === "select") return (
    <div>{label}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5"><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
        <SelectContent>{(field.options || []).filter((o) => o && String(o).trim()).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (field.type === "checkbox") return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={value === true || value === "true"} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#E8712C]" />
      {field.label}
    </label>
  );
  if (field.type === "radio") return (
    <div>{label}
      <div className="mt-1.5 space-y-1.5">
        {(field.options || []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name={key} value={o} checked={value === o} onChange={() => onChange(o)} className="accent-[#E8712C]" />
            {o}
          </label>
        ))}
      </div>
    </div>
  );
  const inputType = { number: "number", date: "date", email: "email", phone: "tel" }[field.type] || "text";
  return <div>{label}<Input type={inputType} {...common} className="mt-1.5" /></div>;
}
